import React, { Component } from 'react';
import {
    Badge,
    Button,
    Col,
    FormControl,
    Grid,
    Glyphicon,
    Modal,
    Pagination,
    Row,
    Table
} from 'react-bootstrap';
import AnalyticsService from '../../../services/AnalyticsService';
import './ReportsManagement.css';

const getReportsFromResponse = (response) => {
    const data = response && response.data;
    const candidates = Array.isArray(response) ? response :
        Array.isArray(data) ? data :
            response && (response.reports || response.items) ||
            data && (data.reports || data.items) ||
            data && data.data && (data.data.reports || data.data.items) || [];
    const pagination = (response && (response.pagination || response.meta)) ||
        (data && (data.pagination || data.meta)) ||
        (data && data.data && (data.data.pagination || data.data.meta)) || {};

    return {
        reports: Array.isArray(candidates) ? candidates : [],
        totalPages: Number(pagination.totalPages || pagination.pages || 1)
    };
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('fr-FR');
};

const getReportId = (report) => report._id || report.id || report.reportId;
const getReportTitle = (report) => report.title || report.name || report.reportName || 'Rapport d’impact sanitaire';

class ReportsManagement extends Component {
    state = {
        reports: [],
        loading: true,
        error: '',
        search: '',
        status: '',
        page: 1,
        totalPages: 1,
        selectedReport: null,
        deletingId: null
    };

    componentDidMount() {
        this.loadReports();
    }

    loadReports = async (page = this.state.page) => {
        this.setState({ loading: true, error: '' });
        try {
            const response = await AnalyticsService.getReports(
                page,
                10,
                this.state.search.trim(),
                this.state.status
            );
            if (response && response.success === false) {
                throw new Error(response.message || response.error || 'Impossible de charger les rapports.');
            }

            const result = getReportsFromResponse(response);
            this.setState({
                reports: result.reports,
                page,
                totalPages: result.totalPages,
                loading: false
            });
        } catch (error) {
            this.setState({
                loading: false,
                error: error.message || 'Une erreur est survenue lors du chargement des rapports.'
            });
        }
    };

    handleSearch = (event) => {
        event.preventDefault();
        this.loadReports(1);
    };

    handleDelete = async (report) => {
        const reportId = getReportId(report);
        if (!reportId || !window.confirm('Supprimer définitivement ce rapport ?')) return;

        this.setState({ deletingId: reportId });
        try {
            const response = await AnalyticsService.deleteReport(reportId);
            if (response && response.success === false) {
                throw new Error(response.message || response.error || 'La suppression du rapport a échoué.');
            }
            this.setState(({ reports }) => ({
                reports: reports.filter(item => getReportId(item) !== reportId),
                deletingId: null,
                selectedReport: null
            }));
        } catch (error) {
            this.setState({
                deletingId: null,
                error: error.message || 'La suppression du rapport a échoué.'
            });
        }
    };

    render() {
        const { reports, loading, error, search, status, page, totalPages, selectedReport, deletingId } = this.state;

        return (
            <div className="content reports-management">
                <Grid fluid>
                    <Row>
                        <Col md={12}>
                            <section className="reports-panel">
                                <header className="reports-header">
                                    <div className="reports-heading">
                                        <span className="reports-heading-icon"><Glyphicon glyph="file" /></span>
                                        <div>
                                            <h1>Rapports générés</h1>
                                            <p>Consultez et gérez les rapports d’impact sanitaire.</p>
                                        </div>
                                    </div>
                                    <Button
                                        className="reports-refresh"
                                        onClick={() => this.loadReports()}
                                        disabled={loading}
                                    >
                                        <Glyphicon glyph="refresh" /> Actualiser
                                    </Button>
                                </header>

                                <form className="reports-filters" onSubmit={this.handleSearch}>
                                    <FormControl
                                        type="search"
                                        value={search}
                                        placeholder="Rechercher un rapport..."
                                        onChange={event => this.setState({ search: event.target.value })}
                                        aria-label="Rechercher un rapport"
                                    />
                                    <FormControl
                                        componentClass="select"
                                        value={status}
                                        onChange={event => this.setState({ status: event.target.value })}
                                        aria-label="Filtrer par statut"
                                    >
                                        <option value="">Tous les statuts</option>
                                        <option value="completed">Terminé</option>
                                        <option value="processing">En cours</option>
                                        <option value="failed">Échec</option>
                                    </FormControl>
                                    <Button type="submit" className="reports-search">
                                        <Glyphicon glyph="search" /> Rechercher
                                    </Button>
                                </form>

                                {error && (
                                    <div className="reports-error" role="alert">
                                        <span>{error}</span>
                                        <Button bsStyle="link" onClick={() => this.loadReports()}>Réessayer</Button>
                                    </div>
                                )}

                                <div className="reports-table-wrap">
                                    <Table responsive hover className="reports-table">
                                        <thead>
                                            <tr>
                                                <th>Rapport</th>
                                                <th>Période</th>
                                                <th>Généré le</th>
                                                <th>Statut</th>
                                                <th className="reports-actions-heading">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr><td colSpan="5" className="reports-state">
                                                    <Glyphicon glyph="refresh" className="reports-spinner" /> Chargement des rapports...
                                                </td></tr>
                                            ) : reports.length === 0 ? (
                                                <tr><td colSpan="5" className="reports-state">
                                                    <Glyphicon glyph="folder-open" />
                                                    <strong>Aucun rapport trouvé</strong>
                                                    <span>Les rapports générés apparaîtront ici.</span>
                                                </td></tr>
                                            ) : reports.map((report, index) => {
                                                const id = getReportId(report);
                                                const statusValue = String(report.status || report.state || 'completed')
                                                    .toLowerCase().replace(/[^a-z0-9-]/g, '');
                                                return (
                                                    <tr key={id || index}>
                                                        <td>
                                                            <div className="report-name">{getReportTitle(report)}</div>
                                                            <div className="report-id">{id || 'Identifiant indisponible'}</div>
                                                        </td>
                                                        <td>{formatDate(report.startDate)} — {formatDate(report.endDate)}</td>
                                                        <td>{formatDate(report.createdAt || report.generatedAt || report.created_at)}</td>
                                                        <td><Badge className={`report-status status-${statusValue}`}>{statusValue}</Badge></td>
                                                        <td className="reports-actions">
                                                            <Button
                                                                bsSize="xs"
                                                                className="report-view"
                                                                onClick={() => this.setState({ selectedReport: report })}
                                                            >
                                                                <Glyphicon glyph="eye-open" /> Détails
                                                            </Button>
                                                            <Button
                                                                bsSize="xs"
                                                                className="report-delete"
                                                                disabled={!id || deletingId === id}
                                                                onClick={() => this.handleDelete(report)}
                                                                aria-label="Supprimer le rapport"
                                                            >
                                                                <Glyphicon glyph="trash" />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>

                                {!loading && totalPages > 1 && (
                                    <div className="reports-pagination">
                                        <span>Page {page} sur {totalPages}</span>
                                        <Pagination
                                            activePage={page}
                                            items={totalPages}
                                            onSelect={selectedPage => this.loadReports(selectedPage)}
                                            maxButtons={5}
                                            boundaryLinks
                                        />
                                    </div>
                                )}
                            </section>
                        </Col>
                    </Row>
                </Grid>

                <Modal
                    show={!!selectedReport}
                    onHide={() => this.setState({ selectedReport: null })}
                    className="report-details-modal"
                >
                    {selectedReport && (
                        <React.Fragment>
                            <Modal.Header closeButton>
                                <Modal.Title>{getReportTitle(selectedReport)}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <dl className="report-details">
                                    <div><dt>Statut</dt><dd>{selectedReport.status || selectedReport.state || 'Terminé'}</dd></div>
                                    <div><dt>Niveau d’agrégation</dt><dd>{selectedReport.aggregationLevel || selectedReport.level || 'Global'}</dd></div>
                                    <div><dt>Début de période</dt><dd>{formatDate(selectedReport.startDate)}</dd></div>
                                    <div><dt>Fin de période</dt><dd>{formatDate(selectedReport.endDate)}</dd></div>
                                    <div><dt>Date de génération</dt><dd>{formatDate(selectedReport.createdAt || selectedReport.generatedAt || selectedReport.created_at)}</dd></div>
                                    {selectedReport.description && <div><dt>Description</dt><dd>{selectedReport.description}</dd></div>}
                                </dl>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button onClick={() => this.setState({ selectedReport: null })}>Fermer</Button>
                            </Modal.Footer>
                        </React.Fragment>
                    )}
                </Modal>
            </div>
        );
    }
}

export default ReportsManagement;
